
import { useState } from 'react';

interface ValidationRules {
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  pattern?: RegExp;
  passwordMatch?: string;
}

interface FormField {
  value: string;
  error: string;
  touched: boolean;
  rules: ValidationRules;
}

type FormState = Record<string, FormField>;

export const useFormValidation = (initialState: Record<string, string>, validationRules: Record<string, ValidationRules>) => {
  // Initialize form state with values, empty errors, untouched fields, and validation rules
  const createInitialFormState = (): FormState => {
    const formState: FormState = {};
    
    for (const key in initialState) {
      formState[key] = {
        value: initialState[key],
        error: '',
        touched: false,
        rules: validationRules[key] || {}
      };
    }
    
    return formState;
  };
  
  const [form, setForm] = useState<FormState>(createInitialFormState());
  
  // Validate a single field
  const validateField = (name: string, value: string): string => {
    const field = form[name];
    if (!field) return '';
    
    const rules = field.rules;
    
    if (rules.required && !value.trim()) {
      return 'Это поле обязательно';
    }
    
    if (rules.minLength && value.length < rules.minLength) {
      return `Минимальная длина ${rules.minLength} символов`;
    }
    
    if (rules.maxLength && value.length > rules.maxLength) {
      return `Максимальная длина ${rules.maxLength} символов`;
    }
    
    if (rules.pattern && !rules.pattern.test(value)) {
      return 'Неверный формат';
    }
    
    if (rules.passwordMatch) {
      const passwordField = form[rules.passwordMatch];
      if (passwordField && value !== passwordField.value) {
        return 'Пароли не совпадают';
      }
    }
    
    return '';
  };
  
  // Handle input changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    setForm(prev => ({
      ...prev,
      [name]: {
        ...prev[name],
        value,
        error: validateField(name, value),
        touched: true
      }
    }));
  };
  
  // Validate all fields
  const validateForm = (): boolean => {
    let isValid = true;
    const newFormState = { ...form };
    
    for (const key in newFormState) {
      const error = validateField(key, newFormState[key].value);
      
      newFormState[key] = {
        ...newFormState[key],
        error,
        touched: true
      };
      
      if (error) {
        isValid = false;
      }
    }
    
    setForm(newFormState);
    return isValid;
  };
  
  // Get form values
  const getValues = (): Record<string, string> => {
    const values: Record<string, string> = {};
    
    for (const key in form) {
      values[key] = form[key].value;
    }
    
    return values;
  };
  
  // Reset the form
  const resetForm = () => {
    setForm(createInitialFormState());
  };
  
  return {
    form,
    handleChange,
    validateForm,
    getValues,
    resetForm
  };
};
